import { useQuery as useZeroQuery } from '@rocicorp/zero/react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { pick } from 'lodash';
import { TagIcon, PlusIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { labelsQuery } from '@/features/pulls/services/pulls';
import { cn, getContrastTextColor } from '@/lib/utils';
import { useZero } from '@/lib/zero';

type Label = {
  name: string;
  color: string;
  description?: string | null;
};

export function LabelManager({ className }: { className?: string }) {
  const { owner, repo, number } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });
  const { data: availableLabels } = useSuspenseQuery(labelsQuery(owner, repo));

  const z = useZero();
  const [open, setOpen] = useState(false);

  const [pull] = useZeroQuery(
    z.query.PullsTable.where('owner', owner).where('repo', repo).where('number', number).one()
  );

  if (!pull) {
    return <div className={cn('text-muted-foreground text-sm', className)}>Loading...</div>;
  }

  const currentLabels: Label[] = pull.labels || [];

  const currentLabelNames = currentLabels.map((label) => label.name);
  const availableToAdd = availableLabels?.filter((label) => label && !currentLabelNames.includes(label.name)) || [];

  // pull is now available
  const handleAddLabel = async (label: Label) => {
    try {
      const newLabels = [...currentLabels, label].map((l) => pick(l, ['name', 'color']));
      console.log('newLabels', newLabels);
      z.mutate.PullsTable.update({
        nodeId: pull.nodeId,
        labels: newLabels,
      });
      setOpen(false);
    } catch (error) {
      toast.error(`Failed to add label "${label.name}"`);
    }
  };

  const handleRemoveLabel = async (labelName: string) => {
    try {
      const newLabels = currentLabels.filter((l) => l.name !== labelName).map((l) => pick(l, ['name', 'color']));
      console.log('newLabels', newLabels);
      z.mutate.PullsTable.update({
        nodeId: pull.nodeId,
        labels: newLabels,
      });
    } catch (error) {
      toast.error(`Failed to remove label "${labelName}"`);
    }
  };

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {/* Current labels */}
      {currentLabels.map((label) => (
        <LabelItem key={label.name} label={label} onClick={() => handleRemoveLabel(label.name)} />
      ))}

      {/* Add label button */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-6 px-2 text-xs">
            <PlusIcon className="h-3 w-3 mr-1" />
            Add label
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0" align="start">
          <Command>
            <CommandInput placeholder="Search labels..." />
            <CommandList>
              <CommandEmpty>No labels found.</CommandEmpty>
              <CommandGroup>
                {availableToAdd.map((label) => (
                  <CommandItem
                    key={label.name}
                    onSelect={() => handleAddLabel(label)}
                    className="flex items-center gap-2"
                  >
                    <LabelItem label={label} />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {currentLabels.length === 0 && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <TagIcon className="h-3 w-3" />
          <span>No labels</span>
        </div>
      )}
    </div>
  );
}

function LabelItem({ label, className, onClick }: { label: Label; className?: string; onClick?: () => void }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'flex items-center px-2 py-0.75 rounded-full border border-border cursor-pointer hover:opacity-75 transition-opacity',
              className
            )}
            onClick={onClick}
            style={{ backgroundColor: `#${label.color}` }}
          >
            <span className="text-xs" style={{ color: getContrastTextColor(label.color) }}>
              {label.name}
            </span>
          </div>
        </TooltipTrigger>

        {label?.description && <TooltipContent>{label?.description}</TooltipContent>}
      </Tooltip>
    </TooltipProvider>
  );
}
